import * as THREE from 'three';
import { createCoreFracture } from './coreFracture';
import { createLivingHeart } from './coreLivingHeart';
import { BOSS_MAX_HP } from './corePhases';

// The board and cinematic arena use the same shell, heart, rings, and shards.
export const createCoreModel = (
  scene,
  {
    hp = BOSS_MAX_HP,
    maxHp = BOSS_MAX_HP,
    position = new THREE.Vector3(0, 0.8, 0),
  } = {}
) => {
  const core = new THREE.Group();
  core.position.copy(position);
  scene.add(core);
  const coreMaterial = new THREE.MeshPhysicalMaterial({
    color: 0x8e45ca,
    emissive: 0x5b1b8d,
    emissiveIntensity: 1.5,
    metalness: 0.5,
    roughness: 0.18,
    clearcoat: 1,
    transparent: true,
    opacity: 0.95,
  });
  const coreMesh = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.64, 2),
    coreMaterial
  );
  coreMesh.castShadow = true;
  coreMesh.receiveShadow = true;
  core.add(coreMesh);
  const fracture = createCoreFracture(
    core,
    scene,
    coreMaterial,
    new THREE.LineBasicMaterial({
      color: 0xe8b7ff,
      transparent: true,
      opacity: 0.72,
    }),
    maxHp
  );
  fracture.syncHealth(hp);
  const heart = createLivingHeart(core);

  const rings = [
    [1.48, 0x8e65ff, 0.75, 0.6],
    [1.82, 0xff75c9, 0.5, -0.85],
    [2.16, 0x63f6ff, 0.34, 0.42],
  ].map(([radius, color, opacity, speed]) => {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(radius, 0.025, 8, 96),
      new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity,
        blending: THREE.AdditiveBlending,
      })
    );
    ring.rotation.set(Math.PI / 2 + radius * 0.13, radius * 0.2, 0);
    ring.userData.speed = speed;
    ring.userData.baseColor = new THREE.Color(color);
    core.add(ring);
    return ring;
  });

  const shardMaterial = new THREE.MeshBasicMaterial({
    color: 0xff84cf,
    transparent: true,
    opacity: 0.8,
    blending: THREE.AdditiveBlending,
  });
  const shards = Array.from({ length: 10 }, (_, index) => {
    const shard = new THREE.Mesh(
      new THREE.BoxGeometry(0.06, 0.48, 0.06),
      shardMaterial
    );
    const angle = (index / 10) * Math.PI * 2;
    shard.position.set(
      Math.cos(angle) * 1.3,
      Math.sin(angle * 1.7) * 0.55,
      Math.sin(angle) * 1.3
    );
    shard.rotation.set(angle, angle * 1.4, angle * 0.5);
    shard.userData.basePosition = shard.position.clone();
    shard.userData.fracturePosition = shard.position
      .clone()
      .multiplyScalar(1.45);
    shard.userData.crownPosition = new THREE.Vector3(
      Math.cos(angle) * 1.42,
      0.7 + Math.sin(angle * 3) * 0.1,
      Math.sin(angle) * 1.42 - 1
    );
    shard.userData.meltdownPosition = shard.userData.crownPosition
      .clone()
      .multiplyScalar(1.12);
    core.add(shard);
    return shard;
  });

  return {
    core,
    coreMesh,
    coreMaterial,
    fracture,
    heart,
    rings,
    shards,
    shardMaterial,
  };
};
